import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  fetchOrganization,
  fetchTeamPlayers,
  fetchInvitations,
  toggleMapping,
  unmapPlayer,
  removeMember,
  revokeInvitation,
  invitePlayer,
  searchPlayerEntities,
} from './api';
import { useAsyncResource, extractApiErrorMessage, getSuspension, unwrapResponse as unwrap } from './hooks';
import { isClubKind, useOrgKind, vocabularyForKind } from './vocabulary';
import SuspendedNotice from './SuspendedNotice';
import { getMediaUrl } from '../../helpers';
import emptyState from '../../images/contactLogo.svg';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (e) { return iso; }
};

const normalizeAcceptUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  if (typeof window === 'undefined') return path;
  return window.location.origin + path;
};

const copyToClipboard = async (text) => {
  if (!text) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
};

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CopyLinkButton = ({ url, label = 'Copy link', $compact }) => {
  const [copied, setCopied] = useState(false);
  const handleClick = async () => {
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };
  return (
    <SmallButton type="button" onClick={handleClick} title={url} $compact={$compact}>
      <ButtonContent>
        <CopyIcon />
        {copied ? 'Copied!' : label}
      </ButtonContent>
    </SmallButton>
  );
};

// Roster + invitations for an organization admin. Rows come from
// /team-admin/players (member-driven since the club admin work): a club
// player who accepted but has not picked his profile yet appears with
// `needs_player_pick` and no mapping. Clubs only "Remove" (decision 14);
// agencies keep the Deactivate/Activate toggle.
const TeamAdminPlayers = () => {
  const storedKind = useOrgKind();
  const org = useAsyncResource(fetchOrganization, []);
  const players = useAsyncResource(fetchTeamPlayers, []);
  const invitations = useAsyncResource(fetchInvitations, []);

  const [pendingAction, setPendingAction] = useState(null);
  const [actionError, setActionError] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [lastAcceptUrl, setLastAcceptUrl] = useState('');
  const [linksByInvitationId, setLinksByInvitationId] = useState({});

  const orgData = unwrap(org.data);
  const kind = orgData?.kind || storedKind;
  const isClub = isClubKind(kind);
  const v = vocabularyForKind(kind, orgData?.name);

  const allRows = unwrap(players.data) || [];
  // Removed club players simply disappear (they can be re-invited); agencies
  // keep seeing deactivated mappings so they can re-activate them.
  const playersList = isClub ? allRows.filter((r) => r.member_active !== false) : allRows;
  const invitationsList = unwrap(invitations.data) || [];
  const suspension = getSuspension(players.error) || getSuspension(invitations.error) || getSuspension(org.error);

  const runAction = async (key, promise, onSuccess) => {
    setPendingAction(key);
    setActionError('');
    try {
      await promise;
      onSuccess?.();
    } catch (err) {
      setActionError(extractApiErrorMessage(err));
    } finally {
      setPendingAction(null);
    }
  };

  const rowKey = (m) => m.member_id ?? m.mapping_id ?? m.user?.id;

  const handleToggle = (mapping) => runAction(
    `toggle-${mapping.mapping_id}`,
    toggleMapping(mapping.mapping_id),
    () => players.reload(),
  );

  const handleRemove = (row) => {
    if (!window.confirm(v.removeConfirm(row.user?.email || 'this user'))) return undefined;
    const request = row.mapping_id
      ? unmapPlayer(row.mapping_id)
      : removeMember(row.user?.id);
    return runAction(
      `remove-${rowKey(row)}`,
      request,
      () => { players.reload(); org.reload(); },
    );
  };

  const handleRevoke = (invitation) => {
    if (!window.confirm(v.revokeConfirm(invitation.email))) return undefined;
    return runAction(
      `revoke-${invitation.id}`,
      revokeInvitation(invitation.id),
      () => invitations.reload(),
    );
  };

  const handleInviteSuccess = (payload) => {
    setShowInviteModal(false);
    const acceptUrl = normalizeAcceptUrl(payload?.accept_url);
    const invitationId = payload?.invitation?.id;
    if (acceptUrl && invitationId) {
      setLinksByInvitationId((prev) => ({ ...prev, [invitationId]: acceptUrl }));
    }
    setLastAcceptUrl(acceptUrl || '');
    invitations.reload();
  };

  const canGenerateLink = (inv) => Boolean(inv?.email) && (isClub || Boolean(inv?.player?.id));

  const handleGenerateLink = async (inv) => {
    if (!canGenerateLink(inv)) return;
    setPendingAction(`link-${inv.id}`);
    setActionError('');
    try {
      const body = isClub ? { email: inv.email } : { player_id: inv.player.id, email: inv.email };
      const res = await invitePlayer(body);
      const acceptUrl = normalizeAcceptUrl(res.data?.accept_url);
      const newId = res.data?.invitation?.id;
      if (acceptUrl && newId) {
        setLinksByInvitationId((prev) => ({ ...prev, [newId]: acceptUrl }));
      }
      if (acceptUrl) await copyToClipboard(acceptUrl);
      invitations.reload();
    } catch (err) {
      setActionError(extractApiErrorMessage(err));
    } finally {
      setPendingAction(null);
    }
  };

  const playerCell = (m) => {
    if (m.player) {
      return (
        <>
          <strong>{m.player.display_name || `${m.player.name || ''} ${m.player.surname || ''}`.trim()}</strong>
          {m.player.positions?.length > 0 && (
            <div style={{ color: '#888', fontSize: 12 }}>
              {m.player.positions.map((p) => p.name || p).join(', ')}
            </div>
          )}
        </>
      );
    }
    if (m.needs_player_pick) {
      return (
        <>
          <Badge $muted>{v.notPicked}</Badge>
          {v.notPickedHint && <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>{v.notPickedHint}</div>}
        </>
      );
    }
    return '—';
  };

  const rowActive = (m) => (isClub ? m.member_active !== false : Boolean(m.is_active));

  return (
    <Wrap>
      <Header>
        <h1>{v.rosterTitle}</h1>
        {!suspension && (
          <InviteButton onClick={() => setShowInviteModal(true)}>{v.inviteButton}</InviteButton>
        )}
      </Header>

      {suspension && <SuspendedNotice data={suspension} vocabulary={v} />}

      {actionError && <Error>{actionError}</Error>}

      {lastAcceptUrl && (
        <Success>
          <SuccessHeader>
            <SuccessTitle>{v.linkReady}</SuccessTitle>
            <DismissLink type="button" onClick={() => setLastAcceptUrl('')}>Dismiss</DismissLink>
          </SuccessHeader>
          <SuccessLinkRow>
            <LinkText href={lastAcceptUrl} target="_blank" rel="noreferrer">{lastAcceptUrl}</LinkText>
            <CopyLinkButton url={lastAcceptUrl} />
          </SuccessLinkRow>
          <SuccessHint>{v.linkHint}</SuccessHint>
        </Success>
      )}

      {!suspension && (
        <>
          <SectionTitle>{v.rosterSection}</SectionTitle>
          {players.loading && <Placeholder>Loading {isClub ? 'squad' : 'roster'}...</Placeholder>}
          {players.error && <Error>Failed to load players.</Error>}
          {!players.loading && !players.error && playersList.length === 0 && (
            <Placeholder>{v.rosterEmpty}</Placeholder>
          )}
          {playersList.length > 0 && (
            <Table>
              <thead>
                <tr>
                  <th>{v.columns.user}</th>
                  <th>{v.columns.player}</th>
                  <th>{v.columns.status}</th>
                  <th>{v.columns.date}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {playersList.map((m) => (
                  <tr key={rowKey(m)}>
                    <td data-label={v.columns.user}>
                      <strong>{[m.user?.first_name, m.user?.last_name].filter(Boolean).join(' ') || m.user?.email}</strong>
                      <div style={{ color: '#888', fontSize: 12 }}>{m.user?.email}</div>
                    </td>
                    <td data-label={v.columns.player}>{playerCell(m)}</td>
                    <td data-label={v.columns.status}>
                      <Badge $active={rowActive(m)}>{rowActive(m) ? v.statusActive : v.statusInactive}</Badge>
                    </td>
                    <td data-label={v.columns.date}>{formatDate(m.mapped_at || m.joined_at)}</td>
                    <td data-label="Actions" className="actions">
                      {!isClub && m.mapping_id && (
                        <SmallButton
                          onClick={() => handleToggle(m)}
                          disabled={pendingAction === `toggle-${m.mapping_id}`}
                        >
                          {m.is_active ? 'Deactivate' : 'Activate'}
                        </SmallButton>
                      )}
                      {rowActive(m) && (
                        <SmallButton
                          $danger
                          onClick={() => handleRemove(m)}
                          disabled={pendingAction === `remove-${rowKey(m)}`}
                        >
                          {v.removeLabel}
                        </SmallButton>
                      )}
                      {!isClub && !rowActive(m) && m.mapping_id && (
                        <SmallButton
                          $danger
                          onClick={() => handleRemove(m)}
                          disabled={pendingAction === `remove-${rowKey(m)}`}
                        >
                          {v.removeLabel}
                        </SmallButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <SectionTitle style={{ marginTop: 32 }}>{v.pendingSection}</SectionTitle>
          {invitations.loading && <Placeholder>Loading invitations...</Placeholder>}
          {invitations.error && <Error>Failed to load invitations.</Error>}
          {!invitations.loading && !invitations.error && invitationsList.length === 0 && (
            <Placeholder>No pending invitations.</Placeholder>
          )}
          {invitationsList.length > 0 && (
            <Table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Player</th>
                  <th>Expires</th>
                  <th>Invited by</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invitationsList.map((inv) => (
                  <tr key={inv.id}>
                    <td data-label="Email">{inv.email}</td>
                    <td data-label="Player">{inv.player?.display_name || (isClub ? <Badge $muted>Picks their own</Badge> : '—')}</td>
                    <td data-label="Expires">{formatDate(inv.expires_at)}</td>
                    <td data-label="Invited by">{[inv.invited_by?.first_name, inv.invited_by?.last_name].filter(Boolean).join(' ') || inv.invited_by?.email || '—'}</td>
                    <td data-label="Actions" className="actions">
                      {linksByInvitationId[inv.id] ? (
                        <CopyLinkButton url={linksByInvitationId[inv.id]} />
                      ) : (
                        <SmallButton
                          type="button"
                          onClick={() => handleGenerateLink(inv)}
                          disabled={pendingAction === `link-${inv.id}` || !canGenerateLink(inv)}
                          title="Revokes the current invitation and issues a fresh link"
                        >
                          <ButtonContent>
                            <CopyIcon />
                            {pendingAction === `link-${inv.id}` ? 'Generating…' : 'Generate link'}
                          </ButtonContent>
                        </SmallButton>
                      )}
                      <SmallButton
                        $danger
                        onClick={() => handleRevoke(inv)}
                        disabled={pendingAction === `revoke-${inv.id}`}
                      >
                        Revoke
                      </SmallButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}

      {showInviteModal && (
        <InviteModal
          isClub={isClub}
          vocabulary={v}
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInviteSuccess}
        />
      )}
    </Wrap>
  );
};

// Agencies pick the platform player up front; clubs invite by e-mail only
// and the player picks himself during onboarding (plan decision 13).
const InviteModal = ({ isClub, vocabulary: v, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isClub) return undefined;
    if (!query || query.length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      searchPlayerEntities(query)
        .then((res) => {
          if (cancelled) return;
          const body = res.data ?? {};
          const list = Array.isArray(body) ? body : (body.results ?? body.data ?? []);
          setResults((Array.isArray(list) ? list : []).slice(0, 12));
        })
        .catch(() => {})
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, isClub]);

  const emailValid = /.+@.+\..+/.test(email);
  const canSubmit = useMemo(
    () => (isClub ? emailValid : (selectedPlayer && emailValid)) && !submitting,
    [isClub, selectedPlayer, emailValid, submitting],
  );

  const handleSubmit = async (event) => {
    event?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const body = isClub
        ? { email: email.trim() }
        : { player_id: selectedPlayer.id, email: email.trim() };
      const res = await invitePlayer(body);
      onSuccess?.(res.data);
    } catch (err) {
      setError(extractApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} as="form">
        <ModalHeader>
          <h2>{v.inviteModalTitle}</h2>
          <Close onClick={onClose} type="button">×</Close>
        </ModalHeader>

        {isClub && v.inviteHelp && <Help>{v.inviteHelp}</Help>}

        {!isClub && (
          <Field>
            <label>Player</label>
            {selectedPlayer ? (
              <SelectedPlayer>
                <PlayerAvatar
                  src={
                    selectedPlayer.photos?.[0]?.full
                      ? getMediaUrl(selectedPlayer.photos[0].full)
                      : emptyState
                  }
                  alt=""
                />
                <PlayerInfo>
                  <PlayerName>
                    {selectedPlayer.display_name || `${selectedPlayer.name || ''} ${selectedPlayer.surname || ''}`.trim()}
                  </PlayerName>
                  <PlayerMeta>
                    {selectedPlayer.nationality?.[0]?.flag && (
                      <Flag
                        src={getMediaUrl(selectedPlayer.nationality[0].flag)}
                        alt=""
                      />
                    )}
                    {selectedPlayer.team?.name && <span>{selectedPlayer.team.name}</span>}
                  </PlayerMeta>
                </PlayerInfo>
                <ClearButton type="button" onClick={() => setSelectedPlayer(null)}>Change</ClearButton>
              </SelectedPlayer>
            ) : (
              <>
                <Input
                  type="text"
                  placeholder="Search player by name..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
                {searching && <Placeholder style={{ padding: 8 }}>Searching...</Placeholder>}
                {results.length > 0 && (
                  <Results>
                    {results.map((p) => (
                      <ResultItem
                        key={p.id}
                        type="button"
                        onClick={() => { setSelectedPlayer(p); setResults([]); setQuery(''); }}
                      >
                        <ResultAvatar
                          src={p.photos?.[0]?.full ? getMediaUrl(p.photos[0].full) : emptyState}
                          alt=""
                        />
                        <ResultBody>
                          <ResultName>
                            {p.display_name || `${p.name || ''} ${p.surname || ''}`.trim()}
                          </ResultName>
                          <ResultMeta>
                            {p.nationality?.[0]?.flag && (
                              <Flag src={getMediaUrl(p.nationality[0].flag)} alt="" />
                            )}
                            {p.team?.name && <span>{p.team.name}</span>}
                            {p.birth_date && <span>{p.birth_date}</span>}
                          </ResultMeta>
                        </ResultBody>
                        <ResultChevron aria-hidden>›</ResultChevron>
                      </ResultItem>
                    ))}
                  </Results>
                )}
              </>
            )}
          </Field>
        )}

        <Field>
          <label>Email</label>
          <Input
            type="email"
            placeholder="player@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={!isClub && !selectedPlayer}
            autoFocus={isClub}
          />
        </Field>

        {error && <Error style={{ marginBottom: 0 }}>{error}</Error>}

        <ModalFooter>
          <SmallButton type="button" onClick={onClose}>Cancel</SmallButton>
          <InviteButton type="submit" disabled={!canSubmit}>
            {submitting ? 'Sending...' : v.inviteSubmit}
          </InviteButton>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

export default TeamAdminPlayers;

const Wrap = styled.div`
  padding: 24px;
  max-width: 1120px;
  margin: 0 auto;

  @media (max-width: 720px) {
    padding: 16px;
  }
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  h1 { margin: 0; }
`;
const SectionTitle = styled.h2`font-size: 16px; margin: 20px 0 12px; color: ${(p) => p.theme.blueish2}; text-transform: uppercase; letter-spacing: 0.08em;`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${(p) => p.theme.row};
  border: 1px solid ${(p) => p.theme.lines};
  border-radius: 12px;
  overflow: hidden;

  th {
    text-align: left;
    padding: 12px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${(p) => p.theme.blueish2};
    border-bottom: 1px solid ${(p) => p.theme.lines};
  }
  td {
    padding: 12px;
    border-bottom: 1px solid ${(p) => p.theme.lines};
    vertical-align: top;
  }
  tr:last-child td { border-bottom: none; }
  td.actions { text-align: right; white-space: nowrap; }

  @media (max-width: 720px) {
    display: block;
    background: transparent;
    border: none;
    border-radius: 0;
    overflow: visible;

    thead { display: none; }
    tbody { display: block; }

    tr {
      display: block;
      background: ${(p) => p.theme.row};
      border: 1px solid ${(p) => p.theme.lines};
      border-radius: 12px;
      padding: 4px 0;
      margin-bottom: 12px;
    }
    tr:last-child { margin-bottom: 0; }

    td {
      display: block;
      padding: 10px 14px;
      border-bottom: 1px solid ${(p) => p.theme.lines};
      text-align: left;
    }
    tr td:last-child { border-bottom: none; }

    td::before {
      content: attr(data-label);
      display: block;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: ${(p) => p.theme.blueish2};
      font-family: 'SatoshiMedium', sans-serif;
      margin-bottom: 4px;
    }

    td.actions {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
    }
    td.actions::before { margin-bottom: 0; }
    td.actions button {
      margin-left: 0;
      width: 100%;
      padding: 10px 12px;
    }
  }
`;
const Badge = styled.span`display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-family: 'SatoshiBold';
  background: ${(p) => (p.$active ? 'rgba(200,255,0,0.2)' : 'rgba(255,255,255,0.1)')};
  color: ${(p) => (p.$active ? p.theme.green : p.theme.blueish2)};
  ${(p) => p.$muted && 'font-family: "SatoshiMedium"; opacity: 0.9;'}`;
const SmallButton = styled.button`
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-family: 'SatoshiBold', sans-serif;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all 0.2s;
  margin-left: 6px;
  background: ${(p) => p.theme.black};
  color: ${(p) => (p.$danger ? p.theme.red : p.theme.blueish)};
  border: 1px solid ${(p) => (p.$danger ? p.theme.red : p.theme.borderColor)};

  &:hover:not(:disabled) {
    border-color: ${(p) => (p.$danger ? p.theme.red : p.theme.green)};
    color: ${(p) => (p.$danger ? p.theme.white : p.theme.green)};
    background: ${(p) => (p.$danger ? p.theme.red : p.theme.black)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const InviteButton = styled.button`padding: 10px 18px; border-radius: 10px; border: none; background: ${(p) => p.theme.green}; color: ${(p) => p.theme.black}; font-family: 'SatoshiBold'; font-size: 14px; cursor: pointer;
  &:hover:not(:disabled) { filter: brightness(0.95); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }`;
const Placeholder = styled.div`padding: 24px; text-align: center; color: ${(p) => p.theme.blueish2};`;
const Help = styled.p`margin: 0; color: ${(p) => p.theme.blueish2}; font-size: 13px; line-height: 1.6;`;
const Error = styled.div`padding: 10px 14px; background: rgba(255, 80, 80, 0.1); border: 1px solid rgba(255, 80, 80, 0.4); border-radius: 10px; color: #ff7070; margin-bottom: 12px; font-size: 14px;`;
const Success = styled.div`
  padding: 12px 14px;
  background: rgba(200, 255, 0, 0.08);
  border: 1px solid rgba(200, 255, 0, 0.3);
  border-radius: 12px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const SuccessHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const SuccessTitle = styled.div`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
  color: ${(p) => p.theme.green};
`;
const SuccessLinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid ${(p) => p.theme.lines};
  border-radius: 8px;
  padding: 8px 10px;

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: stretch;
  }
`;
const SuccessHint = styled.div`
  color: ${(p) => p.theme.blueish2};
  font-size: 12px;
`;
const LinkText = styled.a`
  color: ${(p) => p.theme.green};
  word-break: break-all;
  font-size: 13px;
  flex: 1;
  min-width: 0;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;
const DismissLink = styled.button`
  background: none;
  border: none;
  color: ${(p) => p.theme.blueish2};
  cursor: pointer;
  text-decoration: underline;
  font-size: 13px;
  padding: 0;
  &:hover { color: ${(p) => p.theme.white}; }
`;
const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  svg { flex-shrink: 0; }
`;

const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.75); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 16px;`;
const Modal = styled.div`background: ${(p) => p.theme.black}; border: 1px solid ${(p) => p.theme.lines}; border-radius: 14px; padding: 24px; width: 100%; max-width: 520px; display: flex; flex-direction: column; gap: 14px;`;
const ModalHeader = styled.div`display: flex; justify-content: space-between; align-items: center; h2 { margin: 0; font-size: 20px; }`;
const ModalFooter = styled.div`display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px;`;
const Close = styled.button`background: none; border: none; color: ${(p) => p.theme.white}; font-size: 28px; cursor: pointer; line-height: 1; padding: 0 4px;`;
const Field = styled.div`display: flex; flex-direction: column; gap: 6px;
  label { font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; color: ${(p) => p.theme.blueish2}; }`;
const Input = styled.input`padding: 10px 12px; border-radius: 10px; border: 1px solid ${(p) => p.theme.lines}; background: transparent; color: ${(p) => p.theme.white}; font-size: 14px;
  &:focus { outline: none; border-color: ${(p) => p.theme.green}; }
  &:disabled { opacity: 0.5; }`;
const Results = styled.div`
  border: 1px solid rgba(200, 241, 53, 0.12);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.22);
  max-height: 280px;
  overflow-y: auto;
  margin-top: 6px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const ResultItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: ${(p) => p.theme.white};
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(200, 241, 53, 0.06);
    border-color: rgba(200, 241, 53, 0.16);
    transform: translateX(3px);
  }

  &:hover > span[aria-hidden] {
    opacity: 1;
    transform: translateX(0);
  }
`;
const ResultAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
  background: rgba(200, 241, 53, 0.06);
  flex-shrink: 0;
`;
const ResultBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;
const ResultName = styled.div`
  color: ${(p) => p.theme.white};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ResultMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${(p) => p.theme.blueish2};
  font-size: 12px;
  min-width: 0;
  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const Flag = styled.img`
  width: 16px;
  height: 12px;
  object-fit: cover;
  border-radius: 2px;
  flex-shrink: 0;
`;
const ResultChevron = styled.span`
  color: ${(p) => p.theme.green};
  font-size: 20px;
  line-height: 1;
  margin-left: auto;
  opacity: 0;
  transform: translateX(-5px);
  transition: all 0.2s ease;
`;
const SelectedPlayer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid ${(p) => p.theme.green};
  border-radius: 10px;
  background: rgba(200, 255, 0, 0.05);
`;
const PlayerAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  background: rgba(200, 241, 53, 0.06);
  flex-shrink: 0;
`;
const PlayerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;
const PlayerName = styled.div`
  color: ${(p) => p.theme.white};
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
`;
const PlayerMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${(p) => p.theme.blueish2};
  font-size: 12px;
`;
const ClearButton = styled.button`
  background: none;
  border: none;
  color: ${(p) => p.theme.green};
  cursor: pointer;
  font-size: 13px;
  text-decoration: underline;
  flex-shrink: 0;
`;
